import React, { useState, useEffect } from 'react';
import { FileText, DollarSign, Clock } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const QuotationDashboard = ({ 
  sortedQuotations, 
  quotationStatuses, 
  getStatusDetails, 
  handleViewQuotation,
  setActiveView 
}) => {
  // Use the quotations passed from parent component instead of fetching separately
  const quotations = sortedQuotations || [];
  const loading = false;

  // Group quotations by status
  const quotationsByStatus = {};
  quotationStatuses?.forEach(status => {
    quotationsByStatus[status.id] = quotations.filter(q => q.status_id === status.id);
  });

  // Calculate statistics
  const totalQuotations = quotations.length;
  const acceptedQuotations = quotations.filter(q => q.status_id === 'ACCEPTED').length;
  const pendingQuotations = quotations.filter(q => ['SENT', 'VIEWED'].includes(q.status_id)).length;
  const totalValue = quotations.reduce((sum, q) => sum + (parseFloat(q.total_amount) || 0), 0);
  const acceptedValue = quotations
    .filter(q => q.status_id === 'ACCEPTED')
    .reduce((sum, q) => sum + (parseFloat(q.total_amount) || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading quotation dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-800">報價單儀表板</h2>
      
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">總報價單數</p>
              <p className="text-2xl font-bold text-gray-800">{totalQuotations}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">已接受報價</p>
              <p className="text-2xl font-bold text-gray-800">{acceptedQuotations}</p>
              <p className="text-xs text-gray-500">{totalQuotations > 0 ? Math.round(acceptedQuotations / totalQuotations * 100) : 0}% 接受率</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">待處理報價</p>
              <p className="text-2xl font-bold text-gray-800">{pendingQuotations}</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">總報價金額</p>
              <p className="text-2xl font-bold text-gray-800">${totalValue.toLocaleString()}</p>
              <p className="text-xs text-gray-500">已接受: ${acceptedValue.toLocaleString()}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <DollarSign className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Recent Quotations */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-800">最近報價單</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">報價單號</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">客戶</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">日期</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">金額</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">狀態</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {quotations.slice(0, 5).map((quotation) => {
                const status = getStatusDetails(quotation.status_id);
                return (
                  <tr 
                    key={quotation.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleViewQuotation(quotation)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{quotation.quotation_number}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{quotation.customer_name || '未知客戶'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{quotation.issue_date}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${(quotation.total_amount || 0).toLocaleString()}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                              <span 
                          className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" 
                          style={{ backgroundColor: `${status.color_code}20`, color: status.color_code }}
                        >
                          {status.status_name}
                        </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="p-4 border-t border-gray-200">
          <button 
            className="text-sm font-medium text-blue-600 hover:text-blue-800"
            onClick={() => setActiveView('list')}
          >
            查看全部報價單
          </button>
        </div>
      </div>
      
      {/* Status Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-800">報價單狀態分佈</h3>
          </div>
          <div className="p-6">
            {quotationStatuses.map(status => {
              const count = quotationsByStatus[status.id]?.length || 0;
              const percentage = totalQuotations > 0 ? (count / totalQuotations) * 100 : 0;
              
              return (
                <div key={status.id} className="mb-4 last:mb-0">
                  <div className="flex justify-between mb-1">
                                            <span className="text-sm font-medium text-gray-700">{status.status_name}</span>
                    <span className="text-sm font-medium text-gray-700">{count}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="h-2.5 rounded-full" 
                      style={{ width: `${percentage}%`, backgroundColor: status.color }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-800">即將過期報價單</h3>
          </div>
          <div className="p-6">
            {quotations
              .filter(q => ['SENT', 'VIEWED'].includes(q.status_id))
              .slice(0, 5)
              .map(quotation => {
                const daysUntilExpiry = Math.ceil((new Date(quotation.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
                const status = getStatusDetails(quotation.status_id);
                
                return (
                  <div 
                    key={quotation.id} 
                    className="mb-4 last:mb-0 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleViewQuotation(quotation)}
                  >
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-900">{quotation.quotation_number}</span>
                      <span 
                        className={`px-2 text-xs leading-5 font-semibold rounded-full ${daysUntilExpiry <= 7 ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}
                      >
                        {daysUntilExpiry <= 0 ? '已過期' : `${daysUntilExpiry}天後過期`}
                      </span>
                    </div>
                    <div className="mt-2 text-sm text-gray-500">{quotation.customer_name}</div>
                    <div className="mt-1 text-sm text-gray-500">${quotation.total_amount.toLocaleString()}</div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuotationDashboard;
